import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Chip,
  Divider,
  Group,
  Modal,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { ApiError, scheduleApi, type PlanView } from '../../api';
import { BUNDESLAENDER, WEEKDAYS, formatDate } from '../../format';

/**
 * Everything about a plan that is set once and rarely touched: its name, when
 * it starts, the finish it is measured against, and the working calendar.
 *
 * One dialog with two clearly separated parts rather than settings scattered
 * over the toolbar. Changes are saved together with "Speichern", except
 * calendar exceptions, which apply one at a time as they are added.
 */
export function PlanSettingsModal({
  projectId,
  plan,
  opened,
  onClose,
  onDone,
}: {
  projectId: string;
  plan: PlanView;
  opened: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(plan.name);
  const [startDate, setStartDate] = useState(plan.startDate);
  const [targetFinish, setTargetFinish] = useState(plan.targetFinish ?? '');
  const [bundesland, setBundesland] = useState(plan.calendar.bundesland);
  const [weekdays, setWeekdays] = useState(plan.calendar.workingWeekdays.map(String));
  const [fronleichnam, setFronleichnam] = useState(plan.calendar.fronleichnam);
  const [mariae, setMariae] = useState(plan.calendar.mariaeHimmelfahrt);
  const [exDay, setExDay] = useState('');
  const [exKind, setExKind] = useState<'off' | 'on'>('off');
  const [exNote, setExNote] = useState('');

  useEffect(() => {
    if (!opened) return;
    setName(plan.name);
    setStartDate(plan.startDate);
    setTargetFinish(plan.targetFinish ?? '');
    setBundesland(plan.calendar.bundesland);
    setWeekdays(plan.calendar.workingWeekdays.map(String));
    setFronleichnam(plan.calendar.fronleichnam);
    setMariae(plan.calendar.mariaeHimmelfahrt);
  }, [opened, plan]);

  const save = useMutation({
    mutationFn: async () => {
      await scheduleApi.updatePlan(projectId, { name: name.trim() || plan.name, startDate, targetFinish: targetFinish || null });
      await scheduleApi.updateCalendar(projectId, {
        bundesland,
        workingWeekdays: weekdays.map(Number).sort(),
        fronleichnam,
        mariaeHimmelfahrt: mariae,
      });
    },
    onSuccess: async () => {
      await onDone();
      onClose();
    },
  });
  const addException = useMutation({
    mutationFn: () => scheduleApi.setException(projectId, { day: exDay, kind: exKind, note: exNote.trim() || null }),
    onSuccess: async () => {
      setExDay('');
      setExNote('');
      await onDone();
    },
  });
  const removeException = useMutation({
    mutationFn: (day: string) => scheduleApi.removeException(projectId, day),
    onSuccess: onDone,
  });
  const error = save.error ?? addException.error ?? removeException.error;

  return (
    <Modal opened={opened} onClose={onClose} title="Plan und Kalender" size="lg">
      <Stack gap="lg">
        {error && (
          <Alert color="red">{error instanceof ApiError ? error.message : 'Speichern fehlgeschlagen.'}</Alert>
        )}

        <Stack gap="sm">
          <Title order={5}>Plan</Title>
          <TextInput label="Bezeichnung" placeholder="z. B. Planstand und Version" value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <SimpleGrid cols={{ base: 1, xs: 2 }}>
            <TextInput type="date" label="Projektbeginn" value={startDate} onChange={(e) => setStartDate(e.currentTarget.value)} required />
            <TextInput
              type="date"
              label="Zielende"
              value={targetFinish}
              onChange={(e) => setTargetFinish(e.currentTarget.value)}
            />
          </SimpleGrid>
          <Text size="xs" c="dimmed">Zielende ist optional. Ist es gesetzt, wird der Puffer daran gemessen; ohne Zielende am frühesten Projektende.</Text>
        </Stack>

        <Divider />

        <Stack gap="sm">
          <Title order={5}>Arbeitskalender</Title>
          <Select
            label="Feiertage nach Bundesland"
            data={BUNDESLAENDER}
            value={bundesland}
            onChange={(value) => value && setBundesland(value)}
            allowDeselect={false}
            searchable
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>Arbeitstage</Text>
            <Chip.Group multiple value={weekdays} onChange={(value) => value.length > 0 && setWeekdays(value)}>
              <Group gap={6}>
                {WEEKDAYS.map((day) => (
                  <Chip key={day.value} value={String(day.value)} size="sm" radius="sm">{day.short}</Chip>
                ))}
              </Group>
            </Chip.Group>
          </Stack>
          <Group gap="xl">
            <Switch label="Fronleichnam arbeitsfrei" checked={fronleichnam} onChange={(e) => setFronleichnam(e.currentTarget.checked)} />
            <Switch label="Mariä Himmelfahrt arbeitsfrei" checked={mariae} onChange={(e) => setMariae(e.currentTarget.checked)} />
          </Group>
          <Text size="xs" c="dimmed">
            Beide Feiertage gelten nur in Teilen einzelner Länder und sind deshalb je Projekt wählbar.
          </Text>
        </Stack>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!startDate}>Speichern</Button>
        </Group>

        <Divider />

        <Stack gap="sm">
          <Title order={5}>Ausnahmetage</Title>
          <Text size="xs" c="dimmed">Betriebsurlaub, Brückentage oder ein Samstagstermin. Gilt sofort.</Text>
          {plan.calendar.exceptions.length > 0 && (
            <Table verticalSpacing={4}>
              <Table.Tbody>
                {plan.calendar.exceptions.map((ex) => (
                  <Table.Tr key={ex.day}>
                    <Table.Td w={110}>{formatDate(ex.day)}</Table.Td>
                    <Table.Td w={120}>{ex.kind === 'off' ? 'arbeitsfrei' : 'Arbeitstag'}</Table.Td>
                    <Table.Td c="dimmed">{ex.note}</Table.Td>
                    <Table.Td w={36}>
                      <ActionIcon size="sm" variant="subtle" color="gray" aria-label={`Ausnahme ${formatDate(ex.day)} entfernen`} onClick={() => removeException.mutate(ex.day)}>
                        {'✕'}
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (exDay) addException.mutate();
            }}
          >
            <Group gap="xs" align="flex-end" wrap="wrap">
              <TextInput type="date" label="Tag" value={exDay} onChange={(e) => setExDay(e.currentTarget.value)} w={160} />
              <SegmentedControl
                value={exKind}
                onChange={(value) => setExKind(value as 'off' | 'on')}
                data={[{ value: 'off', label: 'arbeitsfrei' }, { value: 'on', label: 'Arbeitstag' }]}
              />
              <TextInput label="Anlass" value={exNote} onChange={(e) => setExNote(e.currentTarget.value)} style={{ flex: 1, minWidth: 140 }} />
              <Button type="submit" variant="light" disabled={!exDay} loading={addException.isPending}>Hinzufügen</Button>
            </Group>
          </form>
        </Stack>
      </Stack>
    </Modal>
  );
}
